import React, { useMemo, useState } from 'react';
import {
  Flag,
  ToggleRight,
  Layers,
  SlidersHorizontal,
  Search,
  Plus,
  History,
  RefreshCw,
  ChevronsDownUp,
  ChevronsUpDown,
  ShieldAlert,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { FeatureFlag } from '@/client/types/feature-flags';
import { useFeatureFlagListQuery } from '@/client/api/feature-flags/feature-flags.query';
import { useReloadFeatureFlagsMutation } from '@/client/api/feature-flags/feature-flags.mutation';
import {
  buildFlagTree,
  flattenTree,
  collectParentPaths,
  isConditionsEmpty,
  featureFlagPermissionError,
} from '@/client/components/admin/FeatureFlags/utils';
import FeatureFlagsTree from '@/client/components/admin/FeatureFlags/FeatureFlagsTree';
import FeatureFlagEditDrawer from '@/client/components/admin/FeatureFlags/FeatureFlagEditDrawer';
import RevertToDefaultDialog from '@/client/components/admin/FeatureFlags/RevertToDefaultDialog';
import FeatureFlagAuditDrawer from '@/client/components/admin/FeatureFlags/FeatureFlagAuditDrawer';
import AppFeaturesPanel from '@/client/components/admin/FeatureFlags/AppFeaturesPanel';
import { useAdminAnalyticsSummaryQuery } from '@/client/api/analytics/analytics.query';

type FeatureTab = 'flags' | 'features';

const FF_PERMISSION = 'system.feature_flags.manage';

// ─── Stat card ─────────────────────────────────────────────────────────────────

const StatCard = ({
  label,
  value,
  icon,
  color,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}) => (
  <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>{icon}</div>
      <div>
        <p className="text-xs font-medium text-gray-500">{label}</p>
        <p className="text-xl font-semibold text-gray-900">{value}</p>
      </div>
    </div>
  </div>
);

// ─── Restricted / access-denied state ──────────────────────────────────────────

const AccessDenied: React.FC = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Managing feature flags requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{FF_PERMISSION}</code> permission.
      Ask an administrator to grant it.
    </p>
  </div>
);

const FeatureFlagsPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Features', href: '/admin/features/flags' },
    { label: 'Flags' },
  ]);

  const { hasPermission, isSuperAdmin, isLoading: permLoading } = useHasPermission(FF_PERMISSION);
  const { data, isLoading, isFetching, isError, error, refetch } = useFeatureFlagListQuery();
  const reloadMutation = useReloadFeatureFlagsMutation();

  const [tab, setTab] = useState<FeatureTab>('flags');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const canManage = isSuperAdmin || hasPermission;

  const [editFlag, setEditFlag] = useState<FeatureFlag | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [revertFlag, setRevertFlag] = useState<FeatureFlag | null>(null);
  const [historyFlag, setHistoryFlag] = useState<FeatureFlag | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const flags = data?.flags ?? [];

  // 403 from the API OR a definitively-absent permission → access denied.
  const forbidden = (error as any)?.status === 403;
  const permissionKnownAbsent = !permLoading && !isSuperAdmin && !hasPermission;

  // ── Tree / search ──
  const filteredFlags = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return flags;
    return flags.filter(
      (f) =>
        f.name.toLowerCase().includes(term) ||
        (f.description ?? '').toLowerCase().includes(term),
    );
  }, [flags, search]);

  const tree = useMemo(() => buildFlagTree(filteredFlags), [filteredFlags]);

  // While searching, force everything expanded so matches are always visible.
  const effectiveCollapsed = search.trim() ? new Set<string>() : collapsed;
  const rows = useMemo(
    () => flattenTree(tree, effectiveCollapsed),
    [tree, effectiveCollapsed],
  );

  const stats = useMemo(() => {
    const total = flags.length;
    const overrides = flags.filter((f) => f.source === 'override').length;
    const enabled = flags.filter((f) => f.enabled).length;
    const withConditions = flags.filter((f) => !isConditionsEmpty(f.conditions)).length;
    return { total, overrides, enabled, withConditions };
  }, [flags]);

  const toggleCollapse = (path: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      next.has(path) ? next.delete(path) : next.add(path);
      return next;
    });
  };

  const expandAll = () => setCollapsed(new Set());
  const collapseAll = () => setCollapsed(new Set(collectParentPaths(tree)));

  const openCreate = () => {
    setEditFlag(null);
    setEditOpen(true);
  };
  const openEdit = (flag: FeatureFlag) => {
    setEditFlag(flag);
    setEditOpen(true);
  };
  const openHistory = (flag: FeatureFlag) => {
    setHistoryFlag(flag);
  };

  const handleForceRefresh = () => {
    reloadMutation.mutate(undefined, {
      onSuccess: () => toast.success('Serving worker reloaded. Re-fetching flags…'),
      onError: (err: any) =>
        toast.error(featureFlagPermissionError(err) ?? err?.message ?? 'Failed to reload flags.'),
    });
  };

  // ── Gate ──
  if (permissionKnownAbsent || forbidden) {
    return (
      <div className="p-6">
        <AccessDenied />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Flag className="h-6 w-6 text-indigo-500" />
            Flags
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Toggle and configure server-side flags. Writes persist as overrides and propagate to
            all workers within one poll interval (~5s).
          </p>
        </div>
        {tab === 'flags' && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setAuditOpen(true)}>
              <History className="mr-2 h-4 w-4" /> Change log
            </Button>
            <Button
              variant="outline"
              onClick={handleForceRefresh}
              disabled={reloadMutation.isPending}
              title="Advanced — the poller already fans changes to all workers"
            >
              {reloadMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Force refresh
            </Button>
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" /> New override
            </Button>
          </div>
        )}
      </div>

      {/* Tabs: runtime flags vs. the app-feature catalog */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          type="button"
          onClick={() => setTab('flags')}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            tab === 'flags'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Flag className="h-4 w-4" /> Runtime flags
        </button>
        <button
          type="button"
          onClick={() => setTab('features')}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            tab === 'features'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Layers className="h-4 w-4" /> App features
        </button>
      </div>

      {tab === 'features' && <AppFeaturesPanel canManage={canManage} />}

      {tab === 'flags' && (
      <>
      {/* ── Runtime flags ── */}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Total flags"
          value={isLoading ? '—' : stats.total}
          icon={<Flag className="h-5 w-5 text-indigo-600" />}
          color="bg-indigo-50"
        />
        <StatCard
          label="Overrides"
          value={isLoading ? '—' : stats.overrides}
          icon={<Layers className="h-5 w-5 text-amber-600" />}
          color="bg-amber-50"
        />
        <StatCard
          label="Enabled"
          value={isLoading ? '—' : stats.enabled}
          icon={<ToggleRight className="h-5 w-5 text-green-600" />}
          color="bg-green-50"
        />
        <StatCard
          label="With conditions"
          value={isLoading ? '—' : stats.withConditions}
          icon={<SlidersHorizontal className="h-5 w-5 text-blue-600" />}
          color="bg-blue-50"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search flags by name or description…"
            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm outline-none focus:border-gray-400"
          />
        </div>
        <div className="flex items-center gap-2">
          {isFetching && !isLoading && (
            <span className="flex items-center gap-1.5 text-xs text-gray-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> syncing
            </span>
          )}
          <Button variant="outline" size="sm" onClick={expandAll} disabled={!!search.trim()}>
            <ChevronsUpDown className="mr-1.5 h-3.5 w-3.5" /> Expand all
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll} disabled={!!search.trim()}>
            <ChevronsDownUp className="mr-1.5 h-3.5 w-3.5" /> Collapse all
          </Button>
        </div>
      </div>

      {/* Note: enabled is the statically-resolved value */}
      <div className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50/60 p-3 text-xs text-blue-800">
        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <p>
          The <strong>enabled</strong> toggle reflects the statically-resolved value after
          any-level veto — it does <em>not</em> account for per-request conditions. A flag can be
          enabled here yet only reach a slice of users at runtime. See each row&rsquo;s condition
          chips.
        </p>
      </div>

      {/* Tree */}
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/70 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          <span className="w-5" />
          <span className="flex-1">Flag</span>
          <span className="w-9 text-center">On</span>
          <span className="w-[92px] text-right">Actions</span>
        </div>

        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-lg" />
            ))}
          </div>
        ) : isError ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <AlertTriangle className="h-7 w-7 text-red-500" />
            <p className="text-sm text-gray-600">
              {(error as any)?.message ?? 'Failed to load feature flags.'}
            </p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">
            {search.trim() ? `No flags match “${search.trim()}”.` : 'No feature flags found.'}
          </div>
        ) : (
          <FeatureFlagsTree
            rows={rows}
            collapsed={effectiveCollapsed}
            onToggleCollapse={toggleCollapse}
            onEdit={openEdit}
            onRevert={setRevertFlag}
            onHistory={openHistory}
          />
        )}
      </div>
      </>
      )}

      {/* Drawers & dialogs */}
      <FeatureFlagEditDrawer
        isOpen={editOpen}
        flag={editFlag}
        onClose={() => setEditOpen(false)}
      />
      <RevertToDefaultDialog
        isOpen={!!revertFlag}
        flag={revertFlag}
        onClose={() => setRevertFlag(null)}
      />
      <FeatureFlagAuditDrawer
        isOpen={auditOpen}
        onClose={() => setAuditOpen(false)}
      />
      <FeatureFlagAuditDrawer
        isOpen={!!historyFlag}
        filterName={historyFlag?.name}
        onClose={() => setHistoryFlag(null)}
      />
    </div>
  );
};

export default FeatureFlagsPage;
